class plpCard extends HTMLElement {
  private swiper: any = null;
  private variantInputs: NodeListOf<HTMLInputElement> | null = null;

  constructor() {
    super();
  }

  // Lifecycle method called when the element is added to the DOM
  connectedCallback() {
    console.log("plpCard component mounted");
    this.initializeSwiper();
    this.initializeVariantSelection();
    
    const notifyOutOfStockButton = this.querySelector(".js-notify-me");
    if (notifyOutOfStockButton) {
      notifyOutOfStockButton.addEventListener("click", this.handleOutOfStockButton);
    }
  }

  // Lifecycle method called when the element is removed from the DOM
  disconnectedCallback() {
    console.log("plpCard component unmounted");
    
    // Clean up swiper
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    
    // Clean up variant selection listeners
    if (this.variantInputs) {
      this.variantInputs.forEach(input => {
        input.removeEventListener('change', this.handleVariantChange);
      });
    }
    
    const notifyOutOfStockButton = this.querySelector(".js-notify-me");
    if (notifyOutOfStockButton) {
      notifyOutOfStockButton.removeEventListener("click", this.handleOutOfStockButton);
    }
  }

  // Initialize Swiper for product images
  private initializeSwiper() {
    const swiperComponent = this.querySelector('swiper-component');
    console.log('Initializing swiper, found component:', !!swiperComponent);
    
    if (swiperComponent) {
      // Check if navigation elements exist
      const nextButton = swiperComponent.querySelector('.swiper-button-next');
      const prevButton = swiperComponent.querySelector('.swiper-button-prev');
      console.log('Navigation buttons found:', { next: !!nextButton, prev: !!prevButton });
      
      // Wait for swiper component to be initialized
      setTimeout(() => {
        this.swiper = (swiperComponent as any).swiper;
        console.log('Swiper instance after first attempt:', !!this.swiper);
        
        if (this.swiper) {
          console.log('Swiper navigation config:', this.swiper.navigation);
          this.setupSwiperEventListeners();
        }
        
        if (!this.swiper) {
          // If swiper isn't initialized yet, try again
          setTimeout(() => {
            this.swiper = (swiperComponent as any).swiper;
            console.log('Swiper instance after second attempt:', !!this.swiper);
            
            if (this.swiper) {
              console.log('Swiper navigation config (second attempt):', this.swiper.navigation);
              this.setupSwiperEventListeners();
            }
          }, 500);
        }
      }, 200);
    }
  }

  // Setup event listeners for swiper slide changes
  private setupSwiperEventListeners() {
    if (!this.swiper) return;
    
    // Listen for slide change events
    this.swiper.on('slideChange', () => {
      this.handleSwiperSlideChange();
    });
    
    console.log('Swiper event listeners setup complete');
  }

  // Handle swiper slide change and update variant selection
  private handleSwiperSlideChange = () => {
    if (!this.swiper) return;
    
    const activeSlide = this.swiper.slides[this.swiper.activeIndex];
    const variantImageSrc = activeSlide?.dataset?.variantImageSrc;
    const variantId = activeSlide?.dataset?.variantId;
    
    console.log('Swiper slide changed:', {
      activeIndex: this.swiper.activeIndex,
      variantImageSrc,
      variantId
    });
    
    if (variantImageSrc) {
      this.updateVariantSelection(variantImageSrc, variantId);
    }
  }

  // Update variant radio button selection based on swiper slide
  private updateVariantSelection(variantImageSrc: string, variantId?: string) {
    if (!this.variantInputs) return;
    
    // Find the radio button that matches this variant image or variant ID
    let targetInput: HTMLInputElement | null = null;
    
    this.variantInputs.forEach(input => {
      const inputVariantImage = input.dataset.variantImage;
      const inputVariantId = input.dataset.variantId;
      
      // Match by variant ID first (more reliable), then by image src
      if ((variantId && inputVariantId === variantId) || 
          (inputVariantImage === variantImageSrc)) {
        targetInput = input;
      }
    });
    
    if (targetInput && !targetInput.checked) {
      console.log('Updating variant selection to:', targetInput.value);
      targetInput.checked = true;
      
      // Trigger change event to update any other dependent functionality
      const changeEvent = new Event('change', { bubbles: true });
      targetInput.dispatchEvent(changeEvent);
    }
  }

  // Initialize variant selection functionality
  private initializeVariantSelection() {
    this.variantInputs = this.querySelectorAll('.plp-card__variant-options input[type="radio"]');
    
    if (this.variantInputs && this.variantInputs.length > 0) {
      this.variantInputs.forEach((input) => {
        input.addEventListener('change', this.handleVariantChange.bind(this));
      });
    }
  }

  private handleVariantChange = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const variantImage = input.dataset.variantImage;
    const productVariantID = input.dataset.variantId;
    const productVariantPrice = input.dataset.variantPrice;
    const productVariantAvailable = input.dataset.optionAvailable;
    const productCard = input.closest('plp-card');
    const priceContainer = productCard?.querySelector('span[itemprop="price"]');
    const variantInput = productCard?.querySelector('input[name="id"]');
    const addToCartButton = productCard?.querySelector('.plp-card__add-to-cart-button') as HTMLButtonElement;
    const notifyButton = productCard?.querySelector('.js-notify-me') as HTMLButtonElement;
    
    // Update checked state
    this.variantInputs?.forEach((variantInput) => {
      variantInput.classList.remove('checked');
    });
    input.classList.add('checked');
    
    // Update hidden variant ID input
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = productVariantID || '';
    }
    
    // Update price display
    if (priceContainer) {
      priceContainer.textContent = productVariantPrice ?? '';
      const numericPrice = (productVariantPrice ?? '').replace(/[^0-9.,-]/g, '');
      priceContainer.setAttribute('content', numericPrice);
    }
    
    // Handle button visibility based on variant availability
    const isAvailable = productVariantAvailable === 'true';
    
    if (addToCartButton && notifyButton) {
      if (isAvailable) {
        addToCartButton.style.display = '';
        addToCartButton.disabled = false;
        notifyButton.style.display = 'none';
      } else {
        addToCartButton.style.display = 'none';
        notifyButton.style.display = '';
        // Update notify button with current variant ID
        notifyButton.dataset.variantId = productVariantID || '';
      }
    } else if (addToCartButton) {
      // Only add-to-cart button exists
      addToCartButton.disabled = !isAvailable;
    }
    
    console.log('Variant changed:', {
      variantImage,
      variantId: productVariantID,
      available: isAvailable,
      hasSwiper: !!this.swiper
    });
    
    if (variantImage && this.swiper) {
      this.updateSwiperSlide(variantImage);
    } else if (variantImage && !this.swiper) {
      // Try to initialize swiper again if it's not ready
      this.initializeSwiper();
      setTimeout(() => {
        if (this.swiper) {
          this.updateSwiperSlide(variantImage);
        }
      }, 300);
    }
  }

  // Update swiper to show the correct slide for the selected variant
  private updateSwiperSlide(variantImageSrc: string) {
    if (!this.swiper) {
      console.log('No swiper instance available');
      return;
    }
    
    const slides = this.swiper.slides;
    let targetSlideIndex = 0;
    
    console.log('Looking for slide with image:', variantImageSrc);
    console.log('Available slides:', slides.length);
    
    // Find the slide with matching variant image
    for (let i = 0; i < slides.length; i++) {
      const slide = slides[i];
      const slideImageSrc = slide.dataset.variantImageSrc;
      
      console.log(`Slide ${i}:`, slideImageSrc);
      
      if (slideImageSrc === variantImageSrc) {
        targetSlideIndex = i;
        console.log('Found matching slide at index:', targetSlideIndex);
        break;
      }
    }
    
    // Slide to the target slide
    console.log('Sliding to index:', targetSlideIndex);
    this.swiper.slideTo(targetSlideIndex, 300); // 300ms transition
  }

  handleOutOfStockButton = async (event: Event) => {
    console.log("Clicked notify me button inside <plp-card>", event);
    const button = event?.currentTarget as HTMLElement;
    const email = 'ben@shrimpton.agency'; // In production, this should come from user input
    const productId = button.dataset.productId;
    const variantId = button.dataset.variantId;
    const productTitle = button.dataset.productTitle;

    const payload = {
      data: {
        type: "subscription",
        attributes: {
          email,
          // Customize the topic ID if you've created a custom one for BIS
          custom_source: "Shopify BIS Widget",
          profile: {
            source: "Shopify Back In Stock",
            product_title: productTitle,
            shopify_product_id: productId,
            shopify_variant_id: variantId,
          },
        },
      },
    };

    try {
      const response = await fetch("https://a.klaviyo.com/client/subscriptions/back-in-stock", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Klaviyo-API-Key VkVNNS"
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      if (response.ok) {
        alert("You're subscribed! We'll notify you when it's back in stock.");
      } else {
        console.error(result);
        alert("There was an error. Please try again later.");
      }
    } catch (error) {
      console.error(error);
      alert("Network error. Try again later.");
    }

  };
}

export default plpCard;
